import BaseSetup from '@/components/BaseSetup';
import OrderEntity from '@/entity/order/OrderEntity';
import { i18n } from '@/i18n';
import useCandidateService from '@/services/CandidateService';
import useOrderClientProposalService from '@/services/order/OrderClientProposalService';
import { ORDER_PROPOSAL_STATUS } from '@/typings/enums/ORDER_PROPOSAL_STATUS';
import { Reactive } from '@/utils/decorators';
import { nextTick, ref } from 'vue';

export type OrderClientProposalCardSetupEmits = {
	(e: 'select'): void
};
export type OrderClientProposalCardSetupProps = {
	validated?: boolean
	selected?: boolean
	orderClientProposal: IOrderClientProposalWithUserModel
};

export default class OrderClientProposalCardSetup extends BaseSetup {

	static readonly defaultProps = {
		validated: false,
		selected: false,
	};

	@Reactive private readonly state = { skillIndexToCut: undefined as Undef<number> };

	readonly _skills = ref<HTMLElement>();

	private get order () { return this.orderEntity }
	get tempWorkerAddress () {
		const stringToReturn = [];
		if (this.props.orderClientProposal.tempWorkerUser?.personalInformationCity) {
			stringToReturn.push(this.props.orderClientProposal.tempWorkerUser?.personalInformationCity);
		}
		if (this.props.orderClientProposal.tempWorkerUser?.personalInformationPostCode) {
			stringToReturn.push(this.props.orderClientProposal.tempWorkerUser?.personalInformationPostCode);
		}
		if (this.props.orderClientProposal.tempWorkerUser?.personalInformationCountry) {
			stringToReturn.push(this.props.orderClientProposal.tempWorkerUser?.personalInformationCountry);
		}

		return stringToReturn.join(', ');
	}

	get skills () {
		const skillsToReturn: Partial<IProfessionalSkillModel>[] = this.state.skillIndexToCut
			? this.props.orderClientProposal.tempWorkerUser?.professionalSkills.slice(0, (this.state.skillIndexToCut)) ?? []
			: this.props.orderClientProposal.tempWorkerUser?.professionalSkills ?? [];
		if (this.state.skillIndexToCut) {
			skillsToReturn.push(
				{ label: `+${(this.props.orderClientProposal.tempWorkerUser?.professionalSkills.length ?? 0) - (this.state.skillIndexToCut)}` });
		}
		return skillsToReturn;
	}

	get proposalStatus () {
		switch (this.props.orderClientProposal.status) {
			case ORDER_PROPOSAL_STATUS.Waiting:
				return {
					label: this.$t('business.candidateProposedOn', { date: new Date(this.props.orderClientProposal.createdDate).toLocaleDateString(i18n.global.locale.value) }),
					color: 'default' as Orion.Color,
				};
			case ORDER_PROPOSAL_STATUS.Accepted:
				return {
					label: this.props.orderClientProposal.responseDate
						? this.$t('business.acceptedOnDate', { date: new Date(this.props.orderClientProposal.responseDate).toLocaleDateString(i18n.global.locale.value) })
						: this.$t('business.accepted'),
					color: 'success' as Orion.Color,
				};
			case ORDER_PROPOSAL_STATUS.Refused:
				return {
					label: this.props.orderClientProposal.responseDate
						? this.$t('business.refusedOnDate', { date: new Date(this.props.orderClientProposal.responseDate).toLocaleDateString(i18n.global.locale.value) })
						: this.$t('business.refuse'),
					color: 'danger' as Orion.Color,
				};
			default:
				return null;
		}
	}

	constructor (
		private props: OrderClientProposalCardSetupProps,
		private emits: OrderClientProposalCardSetupEmits,
		private orderEntity?: OrderEntity,
	) {
		super();
	}

	protected onMounted () {
		this.setOverflowCounter();
	}

	private setOverflowCounter () {
		if (!this._skills.value) return;

		const skills = this._skills.value;
		const width = skills.getBoundingClientRect().width;
		const gap = +getComputedStyle(skills).gap.replace('px', '');

		nextTick(() => {
			this.getContainerIndexToCut(width, gap);
		});
	}

	private getContainerIndexToCut (width: number, gap: number) {
		let sumOfElementsWidth = 0;
		const container = this._skills;

		[...container.value?.children ?? []].forEach((element, index) => {
			const indexIndicatorWidth = index > 9 ? 35 : 30;
			if ((width - (sumOfElementsWidth + indexIndicatorWidth)) < 0) return;
			sumOfElementsWidth += element.getBoundingClientRect().width + gap;
			if ((width - (sumOfElementsWidth + indexIndicatorWidth)) < 0) {
				this.state.skillIndexToCut = index;
			}
		});
	}

	async acceptCandidateAsync () {
		await useOrderClientProposalService().acceptProposalAsync([this.props.orderClientProposal]);
	}

	async declineCandidateAsync () {
		await useOrderClientProposalService().declineProposalAsync([this.props.orderClientProposal]);
	}

	openCandidateProfile (highlightElement?: string) {
		if (!this.props.orderClientProposal.tempWorkerUser?.id) return;

		useCandidateService().openAsideProfileAsync({
			id: this.props.orderClientProposal.tempWorkerUser.id,
			isValidatedCandidate: this.props.validated,
			orderClientProposal: this.props.orderClientProposal,
			order: this.order,
			highlightElement,
		});
	}

}
